import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation,
  useTransport,
} from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useCallback, useRef } from 'react'

import {
  getEmailAttachmentDownloadUrl,
  getEmailThread,
  listEmails,
  listEmailThreads,
  listEmailThreadsByEntity,
  replyToThread,
  retryEmailBodyFetch,
  retryEmailSend,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetEmailAttachmentDownloadUrlRequestSchema,
  GetEmailThreadRequestSchema,
  ReplyToThreadRequestSchema,
  RetryEmailBodyFetchRequestSchema,
  RetryEmailSendRequestSchema,
} from '@/gen/cortado/v1/emails_pb'
import { invalidateCrossEntityEmailCaches } from './email-thread-cache'
import { organizationStore } from './organization.store'

/**
 * Hook that resolves a short-lived URL for one attachment on an email.
 *
 * The URL is minted on click rather than with the thread, so it never goes
 * stale in the cache. Files on captured mail stream from the connected
 * mailbox; Cortado does not keep a copy.
 */
export function useEmailAttachmentDownloadUrl() {
  const mutation = useMutation(getEmailAttachmentDownloadUrl)

  const getDownloadUrl = async (params: {
    organizationId: string
    emailId: string
    attachmentId: string
  }): Promise<string> => {
    const response = await mutation.mutateAsync(
      create(GetEmailAttachmentDownloadUrlRequestSchema, params),
    )
    if (!response.downloadUrl) throw new Error('No download URL returned')
    return response.downloadUrl
  }

  return { getDownloadUrl }
}

/**
 * Hook to retry fetching a failed inbound email body
 *
 * This hook:
 * - Provides mutation function to retry body fetch via the RetryEmailBodyFetch RPC
 * - Automatically invalidates inbox list and thread detail queries on success
 *
 * @returns Mutation result with retryBodyFetch helper and loading/error states
 */
export function useRetryEmailBodyFetch() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId,
  )

  const mutation = useMutation(retryEmailBodyFetch, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getEmailThread,
          transport,
          cardinality: undefined,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreads,
          transport,
          cardinality: undefined,
        }),
      })
      invalidateCrossEntityEmailCaches(queryClient, transport)
    },
  })

  /**
   * Retry body fetch for an email in the selected organization.
   *
   * @param params - Retry parameters
   * @param params.emailId - The email ID to retry fetching
   * @param params.organizationId - Optional organization override
   */
  const retryBodyFetch = async (params: {
    emailId: string
    organizationId?: string
  }) => {
    const organizationId = params.organizationId || selectedOrganizationId || ''
    const request = create(RetryEmailBodyFetchRequestSchema, {
      organizationId,
      emailId: params.emailId,
    })
    return mutation.mutateAsync(request)
  }

  return {
    mutation,
    retryBodyFetch,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to retry delivery of a failed or bounced outbound email
 *
 * This hook:
 * - Provides mutation function to restart delivery via the RetryEmailSend RPC
 * - Invalidates the queries that surface delivery status so the row updates
 *   without a manual refresh
 * - Resyncs those same queries on `NotFound`/`FailedPrecondition` errors too,
 *   since both mean the local view of the email's state is stale
 *
 * @returns Mutation result with retry helper and loading/error states
 */
export function useRetryEmailSend() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId,
  )

  const invalidateRetryTargets = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listEmailThreadsByEntity,
        transport,
        cardinality: undefined,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: getEmailThread,
        transport,
        cardinality: undefined,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listEmailThreads,
        transport,
        cardinality: undefined,
      }),
    })
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listEmails,
        transport,
        cardinality: undefined,
      }),
    })
  }, [queryClient, transport])

  const mutation = useMutation(retryEmailSend, {
    onSuccess: invalidateRetryTargets,
    onError: (error) => {
      // Not a bug — the email is gone/foreign (NotFound) or already resolved
      // (FailedPrecondition). Either way our cached delivery status is stale.
      if (
        error instanceof ConnectError &&
        (error.code === Code.NotFound || error.code === Code.FailedPrecondition)
      ) {
        invalidateRetryTargets()
      }
    },
  })

  /**
   * Retry delivery for a failed outbound email.
   *
   * @param params - Retry parameters
   * @param params.emailId - The email ID to retry sending
   * @param params.organizationId - Optional organization override
   */
  const retry = useCallback(
    async (params: { emailId: string; organizationId?: string }) => {
      const organizationId =
        params.organizationId || selectedOrganizationId || ''
      const request = create(RetryEmailSendRequestSchema, {
        organizationId,
        emailId: params.emailId,
      })
      return mutation.mutateAsync(request)
    },
    [mutation.mutateAsync, selectedOrganizationId],
  )

  return {
    mutation,
    retry,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

export function useReplyToThread() {
  const queryClient = useQueryClient()
  const transport = useTransport()
  // Per-thread pending idempotency key, mirroring invoices.store.ts/
  // quotes.store.ts's SendInvoice/SendQuote: retries of the same in-flight
  // reply reuse the key; a later, distinct reply to the same thread gets a
  // fresh one once the prior attempt resolves.
  const pendingOperationKeys = useRef(new Map<string, string>())

  const mutation = useMutation(replyToThread, {
    onSuccess: (_response, variables) => {
      if (variables.threadId) {
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getEmailThread,
            transport,
            input: create(GetEmailThreadRequestSchema, {
              id: variables.threadId,
            }),
            cardinality: 'finite',
          }),
        })
      }
      queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: listEmailThreads,
          transport,
          cardinality: undefined,
        }),
      })
      invalidateCrossEntityEmailCaches(queryClient, transport)
    },
  })

  const replyTo = async (params: {
    threadId: string
    fromAccountId: string
    bodyHtml: string
    bodyText?: string
    ccEmails?: string[]
    bccEmails?: string[]
    attachmentIds?: string[]
    /** Org attachment-library IDs to attach alongside uploaded attachments. */
    libraryAttachmentIds?: string[]
    /** Draft this reply was composed in, if any — the server promotes it to the sent message. */
    draftId?: string
    /** Client-generated idempotency key. Defaults to a reused-on-retry generated UUID. */
    idempotencyKey?: string
  }) => {
    const operationKey =
      params.idempotencyKey ??
      pendingOperationKeys.current.get(params.threadId) ??
      crypto.randomUUID()
    pendingOperationKeys.current.set(params.threadId, operationKey)

    const request = create(ReplyToThreadRequestSchema, {
      threadId: params.threadId,
      fromAccountId: params.fromAccountId,
      bodyHtml: params.bodyHtml,
      bodyText: params.bodyText,
      ccEmails: params.ccEmails ?? [],
      bccEmails: params.bccEmails ?? [],
      attachmentIds: params.attachmentIds ?? [],
      libraryAttachmentIds: params.libraryAttachmentIds ?? [],
      draftId: params.draftId,
      idempotencyKey: operationKey,
    })
    const response = await mutation.mutateAsync(request)
    // A later user-initiated reply to this thread is a new logical
    // operation. Retain the key only across failed or ambiguous attempts of
    // the current one.
    pendingOperationKeys.current.delete(params.threadId)
    return response
  }

  return {
    mutation,
    replyTo,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}
